/**
 * useTimeline Hook
 *
 * Fetches production batches with complete timeline data including events, spawn lineage,
 * and substrate lineage from the production_timeline_complete view.
 * Also fetches spawn batches from the spawn_batches table for unified timeline display.
 * Returns batches with their phases, events, variance tracking, and lineage information.
 *
 * Updated: Now reads batch phases from batch_phases table (Production Timeline Unification)
 * Updated: US-C2-010 - Added spawn batch fetching for timeline display
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/supabase-client'

// Phase colors for UI rendering (aligned with ProcessListPage swimlane)
const PHASE_COLORS = {
  // Actual database phase names (from batch_phases.phase_name)
  'Spawn Production': '#f59e0b',   // amber (spawn colonisation)
  'Block Incubation': '#60a5fa',   // light blue (substrate incubation)
  'Primordia Induction': '#60a5fa', // light blue (pinning)
  'First Flush': '#22c55e',        // green (harvest)
  'Flush Interval': '#93c5fd',     // lighter blue (rest)
  'Second Flush': '#16a34a',       // darker green (harvest)
  'Third Flush': '#15803d',        // darkest green (harvest)
  // Legacy/alternative phase names (older processes)
  'Spawn Incubation': '#f59e0b',   // amber
  'Substrate Incubation': '#60a5fa', // light blue
  'Fruiting': '#3b82f6',           // blue
  'Flush 1 Harvest': '#22c55e',    // green
  'Flush 2 Harvest': '#16a34a',    // darker green
  'Flush 3 Harvest': '#15803d',    // darkest green
  'Rest Period': '#93c5fd',        // lighter blue
  // Simplified names
  'Spawn Run': '#f59e0b',          // amber
  'Incubation': '#60a5fa',         // light blue
  'Pinning': '#60a5fa',            // light blue
  'Rest': '#93c5fd',               // lighter blue
  'Liquid Culture': '#8b5cf6',     // purple (LC)
}

// Phase text colors (white with shadow - matches swimlane style)
const PHASE_TEXT_COLORS = {
  // Database phase names
  'Spawn Incubation': '#ffffff',
  'Substrate Incubation': '#ffffff',
  'Fruiting': '#ffffff',
  'Flush 1 Harvest': '#ffffff',
  'Flush 2 Harvest': '#ffffff',
  'Flush 3 Harvest': '#ffffff',
  'Rest Period': '#1e40af',    // darker blue for light background
  // Alternative/simplified names
  'Spawn Run': '#ffffff',
  'Incubation': '#ffffff',
  'Pinning': '#ffffff',
  'Rest': '#1e40af',
  'Liquid Culture': '#ffffff',
  'Spawn Production': '#ffffff'
}

// Phase labels (keep original names from database)
const PHASE_LABELS = {
  'Spawn Incubation': 'Spawn Incubation',
  'Substrate Incubation': 'Substrate Incubation',
  'Fruiting': 'Fruiting',
  'Rest Period': 'Rest Period',
  'Liquid Culture': 'Liquid Culture',
  'Spawn Production': 'Spawn Production',
  'Spawn Colonisation': 'Spawn Colonisation'
}

// Spawn batch colour (purple to distinguish from block batches which use amber for spawn phase)
const SPAWN_BATCH_COLOR = '#8b5cf6'  // purple
const SPAWN_BATCH_TEXT_COLOR = '#ffffff'

/**
 * Hook to fetch production timeline data
 * @param {Object} options - Filter options
 * @param {string} options.status - Filter by batch status
 * @param {string} options.strainId - Filter by strain
 * @param {number} options.limit - Max results (default 50)
 * @returns {Object} { batches, loading, error, refetch }
 */
export function useTimeline(options = {}) {
  const [batches, setBatches] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchBatches = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      // Query the production_timeline_complete view (block batches)
      let blockQuery = supabase
        .from('production_timeline_complete')
        .select('*')
        .order('inoculation_date', { ascending: false, nullsFirst: false })
        .limit(options.limit || 50)

      // Apply filters if provided
      if (options.status) {
        blockQuery = blockQuery.eq('current_state', options.status)
      }

      if (options.strainId) {
        blockQuery = blockQuery.eq('strain_id', options.strainId)
      }

      if (options.offset) {
        blockQuery = blockQuery.range(options.offset, options.offset + (options.limit || 50) - 1)
      }

      // Query spawn batches with strain join (US-C2-010)
      let spawnQuery = supabase
        .from('spawn_batches')
        .select('*, strains:strain_id(id, code, name)')
        .order('inoculation_date', { ascending: false, nullsFirst: false })
        .limit(options.limit || 50)

      if (options.strainId) {
        spawnQuery = spawnQuery.eq('strain_id', options.strainId)
      }

      // Run both queries in parallel
      const [blockResult, spawnResult] = await Promise.all([
        blockQuery,
        spawnQuery
      ])

      if (blockResult.error) {
        throw new Error(`Block batch error: ${blockResult.error.message}`)
      }

      if (spawnResult.error) {
        // Non-fatal: log but continue with block batches only
        console.warn('[useTimeline] Spawn batch fetch failed:', spawnResult.error.message)
      }

      // Transform block batches (existing logic)
      const transformedBlockBatches = (blockResult.data || []).map(batch => ({
        id: batch.id,
        code: batch.batch_code,
        batchType: 'block',
        strain: {
          id: batch.strain_id,
          code: batch.strain_code,
          name: batch.strain_name,
          expected_flush_count: batch.expected_flush_count,
          defaultSpawnRatePercent: batch.default_spawn_rate_percent
        },
        // Shadow batch indicator
        isShadow: batch.is_shadow || false,

        // Demand-pull fields (for shadow batches)
        demandPull: batch.is_shadow ? {
          targetKgFixed: batch.target_kg_fixed,
          targetKgConfidence: batch.target_kg_confidence,
          demandSourceKg: batch.demand_source_kg,
          confidenceTier: batch.demand_confidence_tier,
          demandWeekStart: batch.demand_week_start,
          spawnBagsRequired: batch.spawn_bags_required,
          lastRecalculatedAt: batch.last_recalculated_at,
          linkedSpawnBatch: batch.linked_spawn_batch
        } : null,

        currentState: batch.current_state,
        totalBlocks: batch.total_blocks,
        inoculationDate: batch.inoculation_date,
        colonisationDate: batch.colonisation_date,
        firstHarvestDate: batch.first_harvest_date,
        totalHarvestKg: parseFloat(batch.total_harvest_kg) || 0,
        harvestCount: batch.flush_count || 0,
        isLegacy: batch.is_legacy || false,
        updatedAt: batch.updated_at,

        // Strain variance configuration (for gradient calculations)
        strainVariance: {
          lcDays: batch.typical_lc_days,
          spawnDays: batch.typical_spawn_days,
          incubationDays: batch.typical_incubation_days,
          fruitingDays: batch.typical_fruiting_days
        },

        // Calculated values (for planning future batches)
        calculatedVariance: {
          spawnDays: batch.calculated_spawn_days,
          incubationDays: batch.calculated_incubation_days,
          fruitingDays: batch.calculated_fruiting_days,
          bePercent: batch.calculated_yield_percent
        },

        // Batch phases (NEW: read from batch_phases table)
        phases: (batch.batch_phases || []).map(phase => ({
          phaseId: phase.phase_id,
          name: phase.phase_name,
          label: PHASE_LABELS[phase.phase_name] || phase.phase_name,
          sequenceOrder: phase.sequence_order,
          // Planned dates
          startDate: phase.planned_start ? new Date(phase.planned_start) : null,
          endDate: phase.planned_end ? new Date(phase.planned_end) : null,
          duration: phase.planned_duration,
          // Actual dates
          actualStart: phase.actual_start ? new Date(phase.actual_start) : null,
          actualEnd: phase.actual_end ? new Date(phase.actual_end) : null,
          actualDuration: phase.actual_duration,
          // Status and styling
          status: phase.status,
          color: PHASE_COLORS[phase.phase_name] || '#6b7280',
          textColor: PHASE_TEXT_COLORS[phase.phase_name] || '#374151',
          // Process type from process hierarchy (for bracket grouping)
          processType: phase.process_type || null
        })),

        // Timeline events (parse from JSON) - kept for backwards compatibility
        timelineEvents: (batch.timeline_events || []).map(event => ({
          eventType: event.event_type,
          plannedDate: event.planned_date ? new Date(event.planned_date) : null,
          actualDate: event.actual_date ? new Date(event.actual_date) : null,
          varianceDays: event.variance_days,
          isTask: event.is_task,
          taskStatus: event.task_status,
          notes: event.notes
        })),

        // Spawn lineage (parse from JSON)
        spawnLineage: (batch.spawn_lineage || []).map(spawn => ({
          spawnBatch: {
            id: spawn.spawn_id,
            code: spawn.spawn_code
          },
          bagsUsed: spawn.bags_used,
          bagWeightKg: spawn.bag_weight_kg,
          spawnPerBlockG: spawn.spawn_per_block_g,
          liquidCulture: {
            id: spawn.lc_id,
            code: spawn.lc_code,
            generation: spawn.lc_generation
          }
        })),

        // Substrate lineage (parse from JSON)
        substrateLineage: (batch.substrate_lineage || []).map(substrate => ({
          substrateBatch: {
            id: substrate.substrate_id,
            code: substrate.substrate_code
          },
          blocksAllocated: substrate.blocks_allocated,
          recipeId: substrate.recipe_id
        }))
      }))

      // Transform spawn batches into timeline-compatible format (US-C2-010)
      const transformedSpawnBatches = (spawnResult.data || []).map(sb => {
        const inocDate = sb.inoculation_date ? new Date(sb.inoculation_date) : null
        const readyDate = sb.expected_ready_date
          ? new Date(sb.expected_ready_date)
          : sb.actual_ready_date
            ? new Date(sb.actual_ready_date)
            : inocDate
              ? new Date(new Date(sb.inoculation_date).getTime() + 14 * 24 * 60 * 60 * 1000)
              : null

        // Determine spawn batch status for display
        let displayStatus = 'planned'
        if (sb.status === 'colonised' || sb.status === 'storage' || sb.status === 'used') {
          displayStatus = 'completed'
        } else if (sb.status === 'colonising') {
          displayStatus = 'active'
        } else if (sb.status === 'contaminated' || sb.status === 'discarded') {
          displayStatus = 'completed'
        }

        // Build a single "Spawn Colonisation" phase for the timeline
        const phases = (inocDate && readyDate) ? [{
          phaseId: `spawn-${sb.id}`,
          name: 'Spawn Colonisation',
          label: 'Spawn Colonisation',
          sequenceOrder: 1,
          startDate: inocDate,
          endDate: readyDate,
          duration: Math.round((readyDate - inocDate) / (24 * 60 * 60 * 1000)),
          actualStart: inocDate,
          actualEnd: sb.actual_ready_date ? new Date(sb.actual_ready_date) : null,
          actualDuration: sb.actual_ready_date
            ? Math.round((new Date(sb.actual_ready_date) - inocDate) / (24 * 60 * 60 * 1000))
            : null,
          status: displayStatus === 'completed' ? 'completed' : displayStatus === 'active' ? 'active' : 'planned',
          color: SPAWN_BATCH_COLOR,
          textColor: SPAWN_BATCH_TEXT_COLOR
        }] : []

        return {
          id: sb.id,
          code: sb.code,
          batchType: 'spawn',
          strain: {
            id: sb.strains?.id || sb.strain_id,
            code: sb.strains?.code || '',
            name: sb.strains?.name || 'Unknown'
          },
          isShadow: sb.is_shadow || false,
          demandPull: sb.is_shadow ? {
            targetBags: sb.target_bags,
            demandWeekStart: sb.demand_week_start
          } : null,
          currentState: sb.status,
          bagCount: sb.bag_count,
          bagsRemaining: sb.bags_remaining,
          grainType: sb.grain_type,
          bagWeightKg: sb.bag_weight_kg ? parseFloat(sb.bag_weight_kg) : 2.5,
          inoculationDate: sb.inoculation_date,
          expectedReadyDate: sb.expected_ready_date,
          actualReadyDate: sb.actual_ready_date,
          phases,
          timelineEvents: [],
          spawnLineage: [],
          substrateLineage: [],
          strainVariance: {},
          calculatedVariance: {}
        }
      })

      // Merge both batch types
      setBatches([...transformedBlockBatches, ...transformedSpawnBatches])
    } catch (err) {
      console.error('[useTimeline] Error fetching batches:', err)
      setError(err.message)
      setBatches([])
    } finally {
      setLoading(false)
    }
  }, [options.status, options.strainId, options.limit, options.offset])

  useEffect(() => {
    fetchBatches()
  }, [fetchBatches])

  return {
    batches,
    loading,
    error,
    refetch: fetchBatches
  }
}

/**
 * Hook to fetch a single batch with full details
 * @param {string} batchId - The batch ID to fetch
 * @returns {Object} { batch, loading, error, refetch }
 */
export function useBatchDetail(batchId) {
  const [batch, setBatch] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchBatch = useCallback(async () => {
    if (!batchId) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const { data, error: queryError } = await supabase
        .from('production_timeline_complete')
        .select('*')
        .eq('id', batchId)
        .single()

      if (queryError) {
        if (queryError.code === 'PGRST116') {
          throw new Error('Batch not found')
        }
        throw new Error(`Database error: ${queryError.message}`)
      }

      // Transform snake_case to camelCase and parse JSON fields
      const transformedBatch = {
        id: data.id,
        code: data.batch_code,
        strain: {
          id: data.strain_id,
          code: data.strain_code,
          name: data.strain_name,
          expected_flush_count: data.expected_flush_count
        },
        // Shadow batch indicator
        isShadow: data.is_shadow || false,

        // Demand-pull fields (for shadow batches)
        demandPull: data.is_shadow ? {
          targetKgFixed: data.target_kg_fixed,
          targetKgConfidence: data.target_kg_confidence,
          demandSourceKg: data.demand_source_kg,
          confidenceTier: data.demand_confidence_tier,
          demandWeekStart: data.demand_week_start,
          spawnBagsRequired: data.spawn_bags_required,
          lastRecalculatedAt: data.last_recalculated_at,
          linkedSpawnBatch: data.linked_spawn_batch
        } : null,

        currentState: data.current_state,
        totalBlocks: data.total_blocks,
        inoculationDate: data.inoculation_date,
        colonisationDate: data.colonisation_date,
        firstHarvestDate: data.first_harvest_date,
        totalHarvestKg: parseFloat(data.total_harvest_kg) || 0,
        harvestCount: data.flush_count || 0,
        isLegacy: data.is_legacy || false,
        updatedAt: data.updated_at,

        // Strain variance configuration (for gradient calculations)
        strainVariance: {
          lcDays: data.typical_lc_days,
          spawnDays: data.typical_spawn_days,
          incubationDays: data.typical_incubation_days,
          fruitingDays: data.typical_fruiting_days
        },

        // Calculated values (for planning future batches)
        calculatedVariance: {
          spawnDays: data.calculated_spawn_days,
          incubationDays: data.calculated_incubation_days,
          fruitingDays: data.calculated_fruiting_days,
          bePercent: data.calculated_yield_percent
        },

        // Batch phases (NEW: read from batch_phases table)
        phases: (data.batch_phases || []).map(phase => ({
          phaseId: phase.phase_id,
          name: phase.phase_name,
          label: PHASE_LABELS[phase.phase_name] || phase.phase_name,
          sequenceOrder: phase.sequence_order,
          // Planned dates
          startDate: phase.planned_start ? new Date(phase.planned_start) : null,
          endDate: phase.planned_end ? new Date(phase.planned_end) : null,
          duration: phase.planned_duration,
          // Actual dates
          actualStart: phase.actual_start ? new Date(phase.actual_start) : null,
          actualEnd: phase.actual_end ? new Date(phase.actual_end) : null,
          actualDuration: phase.actual_duration,
          // Status and styling
          status: phase.status,
          color: PHASE_COLORS[phase.phase_name] || '#6b7280',
          textColor: PHASE_TEXT_COLORS[phase.phase_name] || '#374151'
        })),

        // Timeline events (parse from JSON) - kept for backwards compatibility
        timelineEvents: (data.timeline_events || []).map(event => ({
          eventType: event.event_type,
          plannedDate: event.planned_date ? new Date(event.planned_date) : null,
          actualDate: event.actual_date ? new Date(event.actual_date) : null,
          varianceDays: event.variance_days,
          isTask: event.is_task,
          taskStatus: event.task_status,
          notes: event.notes
        })),

        // Spawn lineage (parse from JSON)
        spawnLineage: (data.spawn_lineage || []).map(spawn => ({
          spawnBatch: {
            id: spawn.spawn_id,
            code: spawn.spawn_code
          },
          bagsUsed: spawn.bags_used,
          bagWeightKg: spawn.bag_weight_kg,
          spawnPerBlockG: spawn.spawn_per_block_g,
          liquidCulture: {
            id: spawn.lc_id,
            code: spawn.lc_code,
            generation: spawn.lc_generation
          }
        })),

        // Substrate lineage (parse from JSON)
        substrateLineage: (data.substrate_lineage || []).map(substrate => ({
          substrateBatch: {
            id: substrate.substrate_id,
            code: substrate.substrate_code
          },
          blocksAllocated: substrate.blocks_allocated,
          recipeId: substrate.recipe_id
        }))
      }

      setBatch(transformedBatch)
    } catch (err) {
      console.error('[useBatchDetail] Error fetching batch:', err)
      setError(err.message)
      setBatch(null)
    } finally {
      setLoading(false)
    }
  }, [batchId])

  useEffect(() => {
    fetchBatch()
  }, [fetchBatch])

  return {
    batch,
    loading,
    error,
    refetch: fetchBatch
  }
}

export default useTimeline
